// Hive codec: how the war archive stores a round's Hives compactly.
//
// A Hive is about 60 KB of JSON, almost all of it cells that are still fresh,
// and a round changes only a few cells. So each archived round stores, per
// side:
//   hfBeforeCompact  the Hive before the round, as only the cells that differ
//                    from a fresh cell (each with only its differing fields)
//   hfAfterDelta     the Hive after the round, as only the cells that differ
//                    from hfBefore
// about 4 KB instead of about 120 KB. Each round decodes on its own: nothing
// chains across rounds, so a round missing from the archive costs only itself.
//
// `structures`, `intelLetters` and any other top-level keys are kept whole, in
// their own key order (the order of structures is behaviour: it decides which
// rockets fire).
//
// This is a storage format, so it imports nothing from the rules: each format's
// fresh cell is frozen (rules/subplotGrid.makeSubplot() when it was written).
// A change to the fresh cell in the rules must not change how old archives
// decode; a new format gets a new HIVE_CODEC and its own base.
//   1  2026-10-23
//   2  rules v8: cells carry broodId (Broods left the structures map). A
//      format-1 Hive decodes as it was written, Broods among the structures.
// No imports, and its own package.json says ES module, so the app and the
// Node scripts (scripts/wars.mjs) both load it as is.

export const HIVE_CODEC = 2

const FRESH_V1 = Object.freeze({
    resourceType: null, height: 0, hp: 1, maxHp: 1, destroyed: false, structureId: null,
    revealed: false, scanned: false, distance: null, distanceLetter: null, distanceDead: false, impact: false,
})
const FRESH_V2 = Object.freeze({
    resourceType: null, height: 0, hp: 1, maxHp: 1, destroyed: false, structureId: null, broodId: null,
    revealed: false, scanned: false, distance: null, distanceLetter: null, distanceDead: false, impact: false,
})
const FRESH = { 1: FRESH_V1, 2: FRESH_V2 }
const freshFor = (codec) => {
    if (!FRESH[codec]) throw new Error(`War archive round: unknown hiveCodec ${codec}`)
    return FRESH[codec]
}
const ABSENT = '(absent)'
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)

// One cell's fields that differ from `base`: fresh-cell keys first, extras sorted.
function cellDelta(cell, base, fresh) {
    const out = {}
    const keys = [...Object.keys(fresh), ...Object.keys(cell).filter(k => !(k in fresh)).sort()]
    for (const k of keys) {
        const has = k in cell
        const hadBase = k in base
        if (!has && !hadBase) continue
        if (!has) { out[k] = ABSENT; continue }
        if (hadBase && same(cell[k], base[k])) continue
        out[k] = cell[k]
    }
    for (const k of Object.keys(base).filter(k => !(k in fresh) && !(k in cell)).sort()) out[k] = ABSENT
    return out
}

function applyCellDelta(base, delta) {
    const cell = { ...base }
    for (const [k, v] of Object.entries(delta)) {
        if (v === ABSENT) delete cell[k]
        else cell[k] = v
    }
    return cell
}

// A Hive (object) → its encoding against `base` (a Hive object), or against
// fresh cells when base is null. A value with no subplots array isn't a Hive
// and is kept whole. `codec` picks the fresh cell (default: the current one).
export function encodeHive(hf, base = null, codec = HIVE_CODEC) {
    const fresh = freshFor(codec)
    if (hf == null) return null
    if (!Array.isArray(hf.subplots)) return { whole: hf }
    const cells = {}
    const keys = Object.keys(hf)
    hf.subplots.forEach((cell, s) => {
        const d = cellDelta(cell, base?.subplots?.[s] ?? fresh, fresh)
        if (Object.keys(d).length) cells[s] = d
    })
    const rest = {}
    for (const k of keys) if (k !== 'subplots') rest[k] = hf[k]
    return { count: hf.subplots.length, keys, cells, rest }
}

export function decodeHive(enc, base = null, codec = HIVE_CODEC) {
    if (enc == null) return null
    if ('whole' in enc) return enc.whole
    const fresh = freshFor(codec)
    const subplots = Array.from({ length: enc.count }, (_, s) =>
        applyCellDelta(base?.subplots?.[s] ?? fresh, enc.cells[s] ?? {}))
    const hf = {}
    for (const k of enc.keys) hf[k] = k === 'subplots' ? subplots : enc.rest[k]
    return hf
}

const parse = (v) => (v == null || v === '' ? null : typeof v === 'string' ? JSON.parse(v) : v)

// The archive's fields for one side's Hives, from the live doc's Hive strings
// (either may be null). Stored as JSON strings: the RTDB drops the nulls the
// encoding carries.
export function compactRoundHives(hfBefore, hfAfter) {
    const before = parse(hfBefore)
    const after = parse(hfAfter)
    return {
        hfBeforeCompact: before == null ? null : JSON.stringify(encodeHive(before)),
        hfAfterDelta: after == null ? null : JSON.stringify(encodeHive(after, before)),
    }
}

// The reverse: { hfBefore, hfAfter } as the JSON strings the live doc held.
export function expandRoundHives(side, codec = HIVE_CODEC) {
    const before = decodeHive(parse(side?.hfBeforeCompact), null, codec)
    const after = decodeHive(parse(side?.hfAfterDelta), before, codec)
    return {
        hfBefore: before == null ? null : JSON.stringify(before),
        hfAfter: after == null ? null : JSON.stringify(after),
    }
}

// An archived round entry with its Hives back in full (hfBefore / hfAfter),
// whichever format it was written in. Entries from before the codec pass
// through unchanged.
export function expandRoundEntry(entry) {
    if (!entry || entry.hiveCodec == null) return entry
    freshFor(entry.hiveCodec)
    const { hiveCodec, ...out } = entry
    for (const k of ['p1', 'p2']) {
        if (!out[k]) continue
        const { hfBeforeCompact, hfAfterDelta, ...side } = out[k]
        out[k] = { ...side, ...expandRoundHives({ hfBeforeCompact, hfAfterDelta }, hiveCodec) }
    }
    return out
}
